<template>
  <div class="mini-cart">
    <button class="mini-cart-toggle" @click="toggleCart">
      <img src="/assets/images/mini_cart.svg" alt="mini cart">
      <span v-if="cartData.totalItems" class="mini-cart-count">{{ cartData.totalItems }}</span>
    </button>
    <transition name="slide" mode="out-in">
      <mini-cart-dropdown v-if="showCart"
                          :cartData="cartData"
                          :loading="loading"
                          @close-mini-cart="toggleCart"/>
    </transition>
    <mini-cart-notification/>
  </div>
</template>

<script>
import MiniCartDropdown from '@/components/mini-cart/mini-cart-dropdown'
import MiniCartNotification from '@/components/mini-cart/mini-cart-notification'
import mediaQuery from '@/utils/mediaQuery'
import { getCookie, deleteCookie } from '@/utils/cookieController'
import { lsSave, lsRemove } from '@/utils/localStorageController'
import { fetchCart, deleteFromCart } from '@/api/cart'
import { customEvent } from '@/services/customEventBus'
import { addDataLayerEvent } from '@/services/dataLayer'

export default {
  name: 'mini-cart',
  components: { MiniCartDropdown, MiniCartNotification },

  props: {
    notAllowedInCart: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      showCart: false,
      guid: null,
      cartData: {},
      loading: false,
      deletedEntries: []
    }
  },

  created () {
    this.guid = getCookie('guid')
    if (this.guid) {
      this.fetchCartData(this.guid).then(() => {
        this.emptyCart()
      })
    }
    this.$globalEvent.$on('device-added', payload => {
      if (payload.cartGuid) this.guid = payload.cartGuid
      this.fetchCartData(this.guid).then(() => {
        this.$globalEvent.$emit('cart-fetched', true)
      })
    })
    customEvent.$on('remove-from-cart', payload => {
      this.deleteItemFromCart(payload)
    })
    customEvent.$on('close-mini-cart', () => {
      this.showCart = false
    })
    window.addEventListener('click', this.closeCartOnOutsideClick)
    window.addEventListener('pageshow', e => {
      this.forceDelete(e.persisted)
    })
    // removing local storage here to avoid some edge cases
    // because mini cart is on every page
    lsRemove('ec_show_order_confirmation')
    lsRemove('ec_order_code')
    lsRemove('ec_order_data')
    lsRemove('showNewUserComponent')
  },

  destroyed () {
    this.$globalEvent.$off('device-added')
    customEvent.$off('remove-from-cart')
    customEvent.$off('close-mini-cart')
    window.removeEventListener('click', this.closeCartOnOutsideClick)
  },

  methods: {
    toggleCart () {
      this.showCart = !this.showCart
    },
    closeCartOnOutsideClick (e) {
      if (!e.target.closest('.mini-cart')) {
        this.showCart = false
      }
    },
    fetchCartData (cartId) {
      if (cartId) {
        return fetchCart(cartId).then(({ data }) => {
          this.cartData = data || {}
          this.addPaymentType()
        }).catch(e => {
          if (e.response.data.errors?.[0].reason === 'notFound') {
            deleteCookie('guid')
            this.guid = null
          }
        })
      }
    },
    deleteItemFromCart (payload) {
      this.findDeleted(payload)
      this.loading = true
      const dataToSend = {
        cartId: this.guid,
        entryNumber: payload
      }
      deleteFromCart(dataToSend).then(() => {
        this.fetchCartData(this.guid)
      }).catch(e => {
        this.handleErrorMessage(e.response.data)
      }).finally(() => {
        this.loading = false
      })
    },
    addPaymentType () {
      const device = this.cartData.entries.filter(entry => entry.productOfferingType === 'DEVICE')
      if (!this.cartData.tariffType && device?.[0]?.subscriptionInfo.subscriptionTerm.termOfServiceNumber === 0) {
        lsSave('paymentType', 'one-time')
      } else {
        lsRemove('paymentType')
      }
    },
    emptyCart () {
      const notAllowedTypes = this.$props.notAllowedInCart.split(',')
      const productCode = this.cartData?.entries?.[0]?.product.code
      const entryNumber = this.cartData?.entries?.[0]?.entryNumber
      const pattern = /^[^-]*[^-]/
      const notAllowed = notAllowedTypes.includes(pattern.exec(productCode)[0])
      if (notAllowed) {
        this.deleteItemFromCart(entryNumber)
      }
    },
    forceDelete (fromCache) {
      const notAllowedTypes = this.$props.notAllowedInCart.split(',')
      const productCode = this.cartData?.entries?.[0]?.product.code
      const pattern = /^[^-]*[^-]/
      const notAllowed = notAllowedTypes.includes(pattern.exec(productCode)[0])
      if (this.guid && fromCache && notAllowed) {
        this.showCart = true
        setTimeout(() => {
          customEvent.$emit('force-delete')
        }, 300)
      }
    },
    findDeleted (deletedItem) {
      const deleted = this.cartData.entries.filter(i => i.entryNumber === deletedItem)
      if (deleted[0].entryGroupNumbers.length) {
        this.deletedEntries = this.cartData.entries.filter(i => i.entryGroupNumbers[0] === deleted[0].entryGroupNumbers[0])
      } else {
        this.deletedEntries = deleted
      }
      const dataLayer = this.setDataLayerObjects(this.deletedEntries)
      const category = this.deletedEntries.find(i => i.productOfferingType === 'DEVICE')?.product?.categories?.[0]?.name ||
        this.deletedEntries?.[0]?.product?.categories?.[0]?.name
      addDataLayerEvent({
        event: 'eec.remove',
        ecommerce: {
          add: {
            actionField: {
              list: category
            }
          },
          products: dataLayer
        }
      })
    },
    setDataLayerObjects (deleted) {
      const products = []
      deleted.forEach(item => {
        let monthlyPrice = item?.monthlyPrice?.price.value * item?.monthlyPrice?.cycleEnd
        monthlyPrice = monthlyPrice.toString().replace('.', ',')
        if (item.productOfferingType === 'DEVICE') {
          const oneTimePrice = item?.oneTimePrice?.price.formattedValue
          products.push({
            id: item.product.code,
            name: item.product.name,
            category: item.product.categories?.[1]?.name,
            subcategory: item.product.categories?.[1]?.name,
            variant: item.variant.variantOptionQualifiers?.[0]?.value,
            brand: item.product.brands?.[0]?.name,
            dimension1: item?.monthlyPrice?.cycleEnd || 'one time',
            price: oneTimePrice || monthlyPrice,
            quantity: item.quantity
          })
        } else if (item.productOfferingType === 'TARIFF') {
          products.push({
            id: item.product.code,
            name: item.product.name,
            quantity: item.quantity,
            price: monthlyPrice
          })
        }
      })
      return products
    }
  },
  watch: {
    showCart (newVal) {
      if (newVal && !mediaQuery(768)) {
        document.body.classList.add('vue-modal-open')
        document.getElementsByTagName('html')[0].classList.add('vue-modal-open')
      } else {
        document.body.classList.remove('vue-modal-open')
        document.getElementsByTagName('html')[0].classList.remove('vue-modal-open')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.mini-cart {
  position: absolute;
  right: 7rem;
  top: 1.6rem;
  z-index: 1;

  @media (map_get($breakpoints, sm)) {
    right: 16rem;
  }

  @media (map_get($breakpoints, md)) {
    right: 17.5rem;
  }

  @media (map_get($breakpoints, lg)) {
    right: 18rem;
    top: 50%;
    transform: translateY(-50%);
  }

  @media (map_get($breakpoints, xl)) {
    right: 32rem;
  }
}

.mini-cart-toggle {
  overflow: visible;

  img {
    margin-top: 1px;
    @media (map_get($breakpoints, lg)) {
      margin-top: 0;
      margin-bottom: 3px;
    }
    @media (map_get($breakpoints, xl)) {
      margin-bottom: 2px;
    }
  }
}

.mini-cart-count {
  display: flex;
  justify-content: center;
  position: absolute;
  top: -.5rem;
  right: -1rem;
  height: 2.4rem;
  width: 2.4rem;
  background: $mtel-red;
  border: .2rem solid $white;
  font-size: 1.4rem;
  line-height: 1.9rem;
  box-shadow: 0 .25rem .5rem rgba(0, 0, 0, .32);
  border-radius: 50%;
  color: $white;
}
</style>
